import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useI18n } from '../../lib/i18n'
import { authClient } from '../../lib/auth-client'
import { localizeHeldSlot } from './localized-hold'
import { bookingAccessForSession, customerForSession, persistedBookingDraft, restoredBookingAccess, restoredBookingDraft, updateBookingDraft, type BookingDraft, type RecentBookingAccess } from './booking-draft'

interface BookingState extends BookingDraft {
  recentBookingAccess: RecentBookingAccess | null
}

interface BookingContextValue extends BookingState {
  restored: boolean
  update: (next: Partial<BookingState>) => void
  rememberBookingAccess: (access: Omit<RecentBookingAccess, 'userId'>) => void
  clearCustomerAccess: () => void
  reset: () => void
}

const recentAccessKey = 'seaa_recent_booking_access'
const bookingSessionKey = 'seaa_booking'
const previousStoragePrefix = ['slot', 'flow'].join('')

function migrateSessionValue(currentKey: string, previousKey: string) {
  const current = sessionStorage.getItem(currentKey)
  const previous = sessionStorage.getItem(previousKey)
  if (current === null && previous !== null) sessionStorage.setItem(currentKey, previous)
  sessionStorage.removeItem(previousKey)
  return current ?? previous
}

const initial: BookingState = { ...restoredBookingDraft(null), recentBookingAccess: null }
const BookingContext = createContext<BookingContextValue | null>(null)

export function BookingProvider({ children }: { children: ReactNode }) {
  const { locale } = useI18n()
  const session = authClient.useSession()
  const queryClient = useQueryClient()
  const userId = !session.isPending && !session.isRefetching && !session.error ? session.data?.user.id ?? null : null
  const [state, setState] = useState<BookingState>(initial)
  const [restored, setRestored] = useState(false)
  useEffect(() => {
    try {
      const saved = migrateSessionValue(bookingSessionKey, `${previousStoragePrefix}_booking`)
      const recentAccess = migrateSessionValue(recentAccessKey, `${previousStoragePrefix}_recent_booking_access`)
      const draft = restoredBookingDraft(saved ? JSON.parse(saved) : null)
      const restoredAccess = restoredBookingAccess(recentAccess ? JSON.parse(recentAccess) : null)
      setState({ ...initial, ...draft, recentBookingAccess: restoredAccess })
      if (recentAccess && !restoredAccess) sessionStorage.removeItem(recentAccessKey)
      // Overwrite older drafts immediately to remove previously persisted contact details.
      if (saved) sessionStorage.setItem(bookingSessionKey, JSON.stringify(persistedBookingDraft(draft)))
      for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
        const key = sessionStorage.key(index)
        if (!key?.startsWith('seaa:booking-completion:')) continue
        try {
          const replay = JSON.parse(sessionStorage.getItem(key) ?? 'null') as { signature?: unknown } | null
          if (typeof replay?.signature !== 'string' || !/^[a-f0-9]{64}$/.test(replay.signature)) sessionStorage.removeItem(key)
        } catch { sessionStorage.removeItem(key) }
      }
    } catch {
      // A private browsing policy may disable session storage; booking still works in memory.
    } finally {
      setRestored(true)
    }
  }, [])
  useEffect(() => {
    if (session.isPending || session.isRefetching || session.error) return
    if (state.recentBookingAccess && state.recentBookingAccess.userId !== userId) {
      const token = state.recentBookingAccess.manageToken
      try { sessionStorage.removeItem(recentAccessKey) } catch { /* keep cleared in memory */ }
      queryClient.removeQueries({ queryKey: ['manage', token] })
      setState((current) => ({ ...current, customer: null, customerUserId: null, recentBookingAccess: null }))
    }
  }, [queryClient, session.error, session.isPending, session.isRefetching, state.recentBookingAccess, userId])
  const value = useMemo<BookingContextValue>(() => ({
    ...state,
    customer: customerForSession(state, userId),
    recentBookingAccess: bookingAccessForSession(state.recentBookingAccess, userId),
    hold: state.hold ? { ...state.hold, slot: localizeHeldSlot(state.hold.slot, locale) } : null,
    restored,
    update(next) {
      setState((current) => {
        const updated = updateBookingDraft(current, next)
        try { sessionStorage.setItem(bookingSessionKey, JSON.stringify(persistedBookingDraft(updated))) } catch { /* keep in memory */ }
        return updated
      })
    },
    rememberBookingAccess(access) {
      if (!state.customerUserId) return
      const ownedAccess = { ...access, userId: state.customerUserId }
      try { sessionStorage.setItem(recentAccessKey, JSON.stringify(ownedAccess)) } catch { /* keep in memory */ }
      setState((current) => ({ ...current, recentBookingAccess: ownedAccess }))
    },
    clearCustomerAccess() {
      try { sessionStorage.removeItem(recentAccessKey) } catch { /* keep cleared in memory */ }
      if (state.recentBookingAccess) queryClient.removeQueries({ queryKey: ['manage', state.recentBookingAccess.manageToken] })
      setState((current) => ({ ...current, customer: null, customerUserId: null, recentBookingAccess: null }))
    },
    reset() {
      try { sessionStorage.removeItem(bookingSessionKey); sessionStorage.removeItem(recentAccessKey) } catch { /* keep in memory */ }
      setState(initial)
    },
  }), [locale, queryClient, state, userId, restored])
  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>
}

export function useBooking() {
  const context = useContext(BookingContext)
  if (!context) throw new Error('useBooking must be used inside BookingProvider')
  return context
}
